import React, { useEffect, useState } from 'react'
import 'react-phone-input-2/lib/style.css'
import swal from 'sweetalert'

import CompanyLogo from '../../assets/afbc.png'
import Side3 from '../../assets/investing-signup-images/preamble_brewery.jpg'
import Button from '../../components/Button'
import InvestorUnauthorizedModal from '../../components/Modals/InvestorUnauthorizedModal'
import LoaderModal from '../../components/Modals/LoaderModal'
import Stepper from '../../components/Stepper'
import { IFormProps, InvestingPersonEnum, RetirementAccountEnum } from '../../interfaces'

import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import MessageModal from 'components/Modals/MessageModal'
import { accountFields } from 'constants/accountInfoFields'
import { RiseUpErrors } from 'constants/errors'
import useErrorMessage from 'hooks/useErrorMessage'
import { useGTMData } from 'hooks/useGTMData'
import { useUTMTracking } from 'hooks/useUTMTracking'
import _ from 'lodash'
import queryString from 'query-string'
import { useLocation, useNavigate } from 'react-router-dom'
import { getModifiedUrlWithStep, returnFormErrorString } from 'utils'
import { useRiseUpAPIV1 } from '../../hooks/useRiseUpAPIV1'
import Company from './PersonalInfo/Company'
import Individual from './PersonalInfo/Individual'
import Joint from './PersonalInfo/Joint'
import TrustIra from './PersonalInfo/TrustIra'

export const returnInvestmentType = (type: string) => {
  switch (type) {
    case InvestingPersonEnum.FOR_MYSELF:
      return '(Individual)'
    case InvestingPersonEnum.JOINTLY:
      return '(Joint Account)'
    case InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY:
      return '(Company)'
    case InvestingPersonEnum.RETIREMENT:
      return '(Trust /Retirement Account)'
    default:
      return ''
  }
}

const InvestorPersonalInfo = (props: IFormProps) => {
  const [showPrimary, setShowPrimary] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [selected, setSelected] = useState('')
  const [selectedAuthorized, setSelectedAuthorized] = useState('')
  const [showInvestorUnauthorizedModal, setShowInvestorUnauthorizedModal] = useState(false)

  const sendDataToGTM = useGTMDispatch()

  const { updateOnboardProgress } = useRiseUpAPIV1()
  const { errorMessage, getFriendlyErrorMessage, setErrorMessage } = useErrorMessage()
  const gtmData = useGTMData(props.getValues)
  const {
    getFormattedUTMParams, // Helper to generate a complete GTM event object
  } = useUTMTracking()
  const navigate = useNavigate()
  const location = useLocation()

  const validatePage = async (): Promise<boolean> => {
    if (props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY) {
      let secondaryValues: any[] = [
        'secondary.basicInfo.firstName',
        'secondary.basicInfo.lastName',
        'secondary.addressInfo.country',
        'secondary.personalInfo.phoneNumber',
        'secondary.addressInfo.Street1',
        'secondary.addressInfo.city',
        'secondary.addressInfo.state',
        'secondary.addressInfo.postalCode',
        'secondary.email',
        'basicInfo.firstName',
        'basicInfo.lastName',
        'addressInfo.country',
        'personalInfo.phoneNumber',
        'addressInfo.Street1',
        'addressInfo.city',
        'addressInfo.state',
        'addressInfo.postalCode',
      ]

      // validate all fields using API trigger
      await props.validateFields(secondaryValues)
      // get all fields value for null check (important to validate hidden form fields)
      secondaryValues = props.getValues(secondaryValues)

      return (
        secondaryValues.includes(undefined) ||
        secondaryValues.includes(null) ||
        secondaryValues.includes('') ||
        Boolean(!props.isValid)
      )
    } else if (props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY) {
      let companyValues: any[] = [
        'entityDetails.phoneNumber',
        'entityDetails.countryOfFormation',
        'entityDetails.ein',
        'entityDetails.companyName',
        'entityDetails.regionOfFormation',
        'entityDetails.email',
        'entityDetails.addressInfo.Street1',
        'entityDetails.addressInfo.city',
        'entityDetails.addressInfo.state',
        'entityDetails.addressInfo.postalCode',
        'entityDetails.type',
        'entityDetails.legalStructure',
        'basicInfo.firstName',
        'basicInfo.lastName',
        'addressInfo.country',
        'personalInfo.phoneNumber',
        'addressInfo.Street1',
        'addressInfo.city',
        'addressInfo.state',
        'addressInfo.postalCode',
        'entityDetails.titleWithCompany',
        // 'entityDetails.authorized',
      ]
      // TODO: fix validation for authorized user

      // validate all fields using API trigger
      await props.validateFields(companyValues)

      // remove entityDetails.legalStructure as required fields if entityDetails.type is Other
      if (props.getValues('entityDetails.type') !== 'Other') {
        companyValues = companyValues.filter((e) => e !== 'entityDetails.legalStructure')
      }

      // get all fields value for null check (important to validate hidden form fields)
      companyValues = props.getValues(companyValues)

      return (
        companyValues.includes(undefined) ||
        companyValues.includes(null) ||
        companyValues.includes('') ||
        Boolean(!props.isValid)
      )
    } else if (
      props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
      props.getValues('investingProfile.retirementAccountType') === 'TRUST_ACCOUNT'
    ) {
      let trustValues: any[] = [
        'entityDetails.name',
        'entityDetails.ein',
        'entityDetails.authorized',
        'basicInfo.firstName',
        'basicInfo.lastName',
        'addressInfo.country',
        'personalInfo.phoneNumber',
        'addressInfo.Street1',
        'addressInfo.city',
        'addressInfo.state',
        'addressInfo.postalCode',
        'entityDetails.addressInfo.Street1',
        'entityDetails.addressInfo.country',
        'entityDetails.addressInfo.city',
        'entityDetails.addressInfo.state',
        'entityDetails.addressInfo.postalCode',
      ]

      // validate all fields using API trigger
      await props.validateFields(trustValues)
      // get all fields value for null check (important to validate hidden form fields)
      trustValues = props.getValues(trustValues)

      return (
        trustValues.includes(undefined) ||
        trustValues.includes(null) ||
        trustValues.includes('') ||
        trustValues[1].includes('#') ||
        Boolean(!props.isValid)
      )
    } else if (
      props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
      props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT
    ) {
      let retirementValues: any[] = [
        'entityDetails.name',
        'entityDetails.ein',
        'entityDetails.authorized',
        'basicInfo.firstName',
        'basicInfo.lastName',
        'addressInfo.country',
        'personalInfo.phoneNumber',
        'addressInfo.Street1',
        'addressInfo.city',
        'addressInfo.state',
        'addressInfo.postalCode',
        'entityDetails.nameOfCustodian',
        'entityDetails.emailOfIraContact',
        'entityDetails.addressInfo.Street1',
        'entityDetails.addressInfo.country',
        'entityDetails.addressInfo.city',
        'entityDetails.addressInfo.state',
        'entityDetails.addressInfo.postalCode',
        'entityDetails.addressInfo.regionOfFormation',
      ]

      // validate all fields using API trigger
      await props.validateFields(retirementValues)
      // get all fields value for null check (important to validate hidden form fields)
      retirementValues = props.getValues(retirementValues)

      return (
        retirementValues.includes(undefined) ||
        retirementValues.includes(null) ||
        retirementValues.includes('') ||
        retirementValues[1].includes('#') ||
        Boolean(!props.isValid)
      )
    } else {
      let basicInfo: any[] = [
        'basicInfo.firstName',
        'basicInfo.lastName',
        'addressInfo.country',
        'personalInfo.phoneNumber',
        'addressInfo.Street1',
        'addressInfo.city',
        'addressInfo.state',
        'addressInfo.postalCode',
        'addressInfo.Street2',
        'basicInfo.middleName',
        'basicInfo.suffix',
      ]

      // validate all fields using API trigger
      await props.validateFields(basicInfo)

      // remove not required fields
      basicInfo = basicInfo.filter(
        (e) => !['addressInfo.Street2', 'basicInfo.middleName', 'basicInfo.suffix'].includes(e),
      )

      // get all fields value for null check (important to validate hidden form fields)
      basicInfo = props.getValues(basicInfo)

      return (
        basicInfo.includes(undefined) || basicInfo.includes(null) || basicInfo.includes('') || Boolean(!props.isValid)
      )
    }
  }

  const isSubmitDisabled = (): boolean => {
    const accountType = props.getValues('investingProfile.option')
    let hasNull = true
    if (accountType === InvestingPersonEnum.JOINTLY) {
      hasNull = props.getValues(accountFields.JOINTLY).includes('')
    } else if (accountType === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY) {
      let fields = accountFields.IN_BEHALF_OF_AN_ENTITY

      if (props.getValues('entityDetails.type') !== 'Other') {
        fields = fields.filter((item) => item !== 'entityDetails.legalStructure')
      }

      hasNull = props.getValues(fields).includes('')
    } else if (
      accountType === InvestingPersonEnum.RETIREMENT &&
      props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.TRUST_ACCOUNT
    ) {
      let fields = accountFields.RETIREMENT_TRUST_ACCOUNT
      if (showPrimary) {
        fields = fields.filter((item) => item !== 'entityDetails.addressInfo.postalCode')
      }

      hasNull = props.getValues(fields).includes('')
    } else if (
      accountType === InvestingPersonEnum.RETIREMENT &&
      props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT
    ) {
      let fields = accountFields.RETIREMENT_ACCOUNT

      if (showPrimary) {
        fields = fields.filter((item) => item !== 'entityDetails.addressInfo.postalCode')
      }

      hasNull = props.getValues(fields).includes('')
    } else if (accountType === InvestingPersonEnum.FOR_MYSELF) {
      hasNull = props.getValues(accountFields.FOR_MYSELF).includes('')
    }
    return hasNull
  }

  const handleTrustSelection = (e: any) => {
    setSelected(e.target.value)
    props.setValue('entityDetails.authorized', e.target.value)
    props.validateFields(['entityDetails.authorized'])
    props.clearErrors('entityDetails.authorized')
  }

  const handleRetirementSelection = (e: any) => {
    setSelected(e.target.value)
    props.setValue('entityDetails.authorized', e.target.value)
    props.validateFields(['entityDetails.authorized'])
    props.clearErrors('entityDetails.authorized')
  }

  const handleAuthorizedSelection = (e: any) => {
    setSelectedAuthorized(e)
    props.setValue('entityDetails.authorized', e)
    props.clearErrors('entityDetails.authorized')
  }

  /**
   * NOTE: Enable this function when the field "entityDetails.establishedOn" is enabled
   */
  // const onChangeCompanyEstablished: DatePickerProps['onChange'] = (date, dateString) => {
  //   if (dateString !== '') {
  //     props.setValue('entityDetails.establishedOn', dayjs(dateString))
  //   }
  // }

  const returnPropsErrors = (): any => {
    switch (props.getValues('investingProfile.option')) {
      case 'FOR_MYSELF':
        return props?.errors
      case InvestingPersonEnum.JOINTLY:
        if (showPrimary) {
          return props?.errors
        }
        return props?.errors?.secondary
      case InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY:
        if (showPrimary) {
          return props?.errors
        }
        return props?.errors
      case InvestingPersonEnum.RETIREMENT:
        if (showPrimary) {
          return props?.errors
        } else if (
          !showPrimary &&
          props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT
        ) {
          return props?.errors?.entityDetails
        }
        return props?.errors?.entityDetails
      default:
        return props?.errors
    }
  }

  const flattenObject = (): any => {
    const obj = returnPropsErrors()
    let arr = { ...obj } // copy

    if (obj?.basicInfo || obj?.basicInfo?.length) {
      delete arr.basicInfo
      // delete unneccessary arrays
      arr = { ...arr, ...obj.basicInfo }
    }
    if (obj?.personalInfo || obj?.personalInfo?.length) {
      delete arr.personalInfo
      // delete unneccessary arrays
      arr = { ...arr, ...obj.personalInfo }
    }
    if (obj?.addressInfo || obj?.addressInfo?.length) {
      delete arr.addressInfo
      // delete unneccessary arrays
      arr = { ...arr, ...obj.addressInfo }
    }

    if (obj?.addressInfo || obj?.addressInfo?.length) {
      delete arr.addressInfo
      // delete unneccessary arrays
      arr = { ...arr, ...obj.addressInfo }
    }

    if (obj?.entityDetails || obj?.entityDetails?.length) {
      delete arr.entityDetails
      // delete unneccessary arrays
      arr = { ...arr, ...obj.entityDetails }
    }
    return arr
  }

  const handleSubmit = async () => {
    setShowInvestorUnauthorizedModal(false)
    // nothing happens if fields has errors (returns nothing)
    // display warning notif
    const isFormInvalid = await validatePage()

    // removed authorized in required fields then checked value instead
    const isNotAuthorized = () => {
      const needAuthorization =
        props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY ||
        props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT
      if (needAuthorization && !props.getValues('entityDetails.authorized')) {
        return true
      }
      return false
    }

    const secondFormHasError =
      _.isEmpty(await props.errors) &&
      props.getValues('investingProfile.option') !== InvestingPersonEnum.FOR_MYSELF &&
      !isNotAuthorized()

    secondFormHasError && setShowPrimary(false)

    if (isFormInvalid || isNotAuthorized()) {
      isNotAuthorized() && setShowPrimary(true)
      setErrorMessage(
        isNotAuthorized()
          ? `Please select "Yes or No" if you are authorize for this investment.`
          : _.isEmpty(await props.errors) && props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY
            ? `Please select “Second Investor” tab and provide investor information for your second investor.`
            : props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
              ? `Please select “Company” tab and provide company information.`
              : props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
                  props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.TRUST_ACCOUNT
                ? `Please select “Trust Account” tab and provide trust account information.`
                : props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
                    props.getValues('investingProfile.retirementAccountType') ===
                      RetirementAccountEnum.RETIREMENT_ACCOUNT
                  ? `Please select “IRA” tab and provide IRA information.`
                  : returnFormErrorString(flattenObject()),
      )

      // close all Unauthorize Modal if has errors
      setShowInvestorUnauthorizedModal(false)

      return
    }

    setIsLoading(true)

    const formattedUTMParams = getFormattedUTMParams()
    const pathname = location.pathname
    const currentParams = queryString.parse(location.search)
    const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'social_security_num')

    try {
      props.setValue('personalInfo.phoneNumber', props.getValues('personalInfo.phoneNumber').replace(/[^0-9]+/g, ''))
      const data = {
        ...props.getValues(),
        lastCompletedStage: '4',
      }
      const updateOnboardProgressRes = await updateOnboardProgress(data)

      if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
        sendDataToGTM({
          event: 'add_shipping_info',
          step_title: 'INVESTOR_PERSONAL_INFO',
          ...gtmData,
          ...formattedUTMParams,
        })
        navigate(updatedUrl)
        props.nextStep('4')
      }
    } catch (error: any) {
      setIsLoading(false)
      // catch the duplicate errors
      if (error.response.data === RiseUpErrors.DuplicatePhoneNumber) {
        setErrorMessage(RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.')
        setIsLoading(false)
        return
      }
      if (error.response.data === RiseUpErrors.DuplicateSSN) {
        setErrorMessage(RiseUpErrors.DuplicateSSN + ' Please update the ssn.')
        setIsLoading(false)
        return
      }
      swal({
        title: 'Error!',
        text: 'Please check if your details are valid and correct. If error persists, contact us.',
        icon: 'error',
      })
    }
  }

  // auto populate company's email and phone number from authorized user's info
  useEffect(() => {
    _.isEmpty(props.getValues('entityDetails.email')) && props.setValue('entityDetails.email', props.getValues('email'))
    _.isEmpty(props.getValues('entityDetails.phoneNumber')) &&
      props.setValue('entityDetails.phoneNumber', props.getValues('personalInfo.phoneNumber'))

    // add zip/ postal code in state for empty form validation
    !props.getValues('addressInfo.postalCode') && props.setValue('addressInfo.postalCode', '')
    ;(props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY ||
      props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT) &&
      !props.getValues('entityDetails.addressInfo.postalCode') &&
      props.setValue('entityDetails.addressInfo.postalCode', '')
  }, [showPrimary]) // eslint-disable-line

  useEffect(() => {
    props.setCurrentStage(1)

    if (props.getValues('addressInfo.country')) {
      props.setValue('addressInfo.country', 'USA')
      props.setValue('citizenshipInfo.country', 'United States')
      props.setValue('citizenshipInfo.citizenship', 'U.S. Citizen')

      if (props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY) {
        props.setValue('secondary.addressInfo.country', 'USA')
        props.setValue('secondary.citizenshipInfo.country', 'United States')
        props.setValue('secondary.citizenshipInfo.citizenship', 'U.S. Citizen')
      } else {
        props.setValue('entityDetails.countryOfFormation', 'USA')
        props.setValue('entityDetails.addressInfo.country', 'USA')
        props.setValue('entityDetails.citizenshipInfo.country', 'United States')
        props.setValue('entityDetails.citizenshipInfo.citizenship', 'U.S. Citizen')
      }
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    if (props.getValues('entityDetails.authorized')) {
      setSelected(props.getValues('entityDetails.authorized'))
    }
  }, [props.getValues('entityDetails.authorized')]) // eslint-disable-line

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side3} alt={`${Side3}`} />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        {props.getValues('investingProfile.option') === InvestingPersonEnum.FOR_MYSELF && (
          <Individual {...props} showPrimary={showPrimary} />
        )}

        {props.getValues('investingProfile.option') === InvestingPersonEnum.JOINTLY && (
          <Joint {...props} showPrimary={showPrimary} setShowPrimary={setShowPrimary} />
        )}

        {props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY && (
          <Company
            {...props}
            showPrimary={showPrimary}
            setShowPrimary={setShowPrimary}
            selectedAuthorized={selectedAuthorized}
            handleAuthorizedSelection={handleAuthorizedSelection}
          />
        )}

        {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT && (
          <TrustIra
            {...props}
            showPrimary={showPrimary}
            setShowPrimary={setShowPrimary}
            selected={selected}
            handleTrustSelection={handleTrustSelection}
            handleRetirementSelection={handleRetirementSelection}
            handleAuthorizedSelection={handleAuthorizedSelection}
          />
        )}

        <div className="w-full justify-center gap-3 py-10 sm:block lg:flex">
          <div className="flex flex-col sm:w-[100%] lg:w-[20%]">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('2')} />
          </div>
          <div className="flex flex-col sm:w-[100%]">
            <Button
              btnName={`Next: More Investor info ${returnInvestmentType(
                props.getValues('investingProfile.option') || '',
              )}`}
              btnType={'button'}
              disabled={isSubmitDisabled()}
              onClickHandler={() => {
                const isTrustAccountUnauthorized = () => {
                  return (
                    props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
                    props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.TRUST_ACCOUNT &&
                    props.getValues('entityDetails.authorized') === 'no'
                  )
                }

                const isRetirementAccountUnauthorized = () => {
                  return (
                    props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
                    props.getValues('investingProfile.retirementAccountType') ===
                      RetirementAccountEnum.RETIREMENT_ACCOUNT &&
                    props.getValues('entityDetails.authorized') === 'no'
                  )
                }

                const isInBehalfOfEntityUnauthorized = () => {
                  return (
                    props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY &&
                    props.getValues('entityDetails.authorized') === 'no'
                  )
                }

                if ((isTrustAccountUnauthorized() || isRetirementAccountUnauthorized()) && showPrimary) {
                  setShowInvestorUnauthorizedModal(true)
                } else {
                  handleSubmit()
                }
              }}
              // disabled={!props.isValid}
              // disabled={validatePage()}
            />
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading} />
      <InvestorUnauthorizedModal
        isVisible={showInvestorUnauthorizedModal}
        onProceed={() => {
          handleSubmit()
        }}
        onCancel={() => {
          setShowInvestorUnauthorizedModal(false)
        }}
        data={props.getValues()}
      />
      <MessageModal
        isVisible={errorMessage !== ''}
        onProceed={() => {
          // automatically set the step to appropriate form duplicate error
          if (errorMessage === RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.') {
            setErrorMessage('')
            props.nextStep('3')
          }
          if (errorMessage === RiseUpErrors.DuplicateSSN + ' Please update the ssn.') {
            setErrorMessage('')
            props.nextStep('4')
          }
          setErrorMessage('')
        }}
        onCancel={() => {
          setErrorMessage('')
        }}
        title={'Error'}
        description={errorMessage}
        type={'error'}
        btn2={'Ok'}
      />
    </div>
  )
}

export default InvestorPersonalInfo
